import { getAction, postAction, putAction, deleteAction, downFile } from '@/api/manage'
import { getBillTypeFromNo, normalizeBillType } from '@/utils/billTypeUtils'

const PLUGIN_API = '/api/plugin/print-template-plugin/plugin-print-template'
// 新的billPrint路径（匹配参考系统）
const BILLPRINT_API = '/api/plugin/print-template-plugin/billPrint'

// ========== 模板管理 ==========

/**
 * 获取模板列表（兼容旧接口名）
 */
export const getTemplateList = (params) => {
  return getList(params)
}

/**
 * 获取模板列表
 */
export const getList = (params) => {
  return getAction(PLUGIN_API + '/list', params)
}

/**
 * 获取模板详情
 */
export const getTemplateDetail = (id) => {
  return getAction(PLUGIN_API + '/' + id)
}

/**
 * 创建模板
 */
export const createTemplate = (data) => {
  return postAction(PLUGIN_API, data)
}

/**
 * 更新模板
 */
export const updateTemplate = (id, data) => {
  return putAction(PLUGIN_API + '/' + id, data)
}

/**
 * 删除模板
 */
export const deleteTemplate = (id) => {
  return deleteAction(PLUGIN_API + '/' + id)
}

/**
 * 启用/禁用模板
 */
export const toggleTemplate = (id, enabled) => {
  return putAction(PLUGIN_API + '/' + id + '/toggle?enabled=' + enabled)
}

/**
 * 设置为默认模板
 */
export const setDefaultTemplate = (id) => {
  return putAction(PLUGIN_API + '/' + id + '/set-default')
}

/**
 * 按单据类型获取模板
 */
export const getTemplateByBillType = (billType, tenantId) => {
  return getAction(PLUGIN_API + '/by-bill-type', { billType, tenantId })
}

/**
 * 预览模板
 */
export const previewTemplate = (id, no) => {
  return getAction(PLUGIN_API + '/preview/' + id, { no })
}

// ========== 字段配置 ==========

/**
	 * 获取可用字段列表
	 */
export const getTemplateFields = (billType) => {
  return getAction(PLUGIN_API + '/fields', { billType })
}

// ========== 打印渲染 ==========

/**
 * 获取打印URL
 */
export const getPrintUrl = (no, type, templateId) => {
  let url = PLUGIN_API + '/render?no=' + encodeURIComponent(no) + '&type=' + encodeURIComponent(type)
  if (templateId) {
    url += '&templateId=' + templateId
  }
  return url
}

/**
 * 复制模板
 */
export const copyTemplate = (id) => {
  return postAction(PLUGIN_API + '/' + id + '/copy')
}

/**
 * 导出模板
 */
export const exportTemplates = (params) => {
  return downFile(PLUGIN_API + '/export', params)
}

// ========== 批量导入 ==========

/**
 * 从文件目录批量导入模板
 * @param {Object} params - { directory: string } 可选，默认目录为 D:\JSH_ERP\辅助资料\打印模板
 */
export const importTemplatesFromFiles = (params = {}) => {
  return postAction(PLUGIN_API + '/import-from-files', params)
}

/**
 * 导入单个模板文件
 * @param {Object} params - { filePath, billType, templateName }
 */
export const importSingleTemplate = (params) => {
  return postAction(PLUGIN_API + '/import-single', params)
}

// ========== 增强预览 ==========

/**
 * 获取模板预览（带真实数据）
 * @param {number} id - 模板ID
 * @param {Object} params - { billNo, billType }
 */
export const previewTemplateWithData = (id, params = {}) => {
  return getAction(PLUGIN_API + '/preview/' + id, params)
}

/**
 * 获取单据的真实数据
 * @param {string} billNo - 单据编号
 * @param {string} billType - 单据类型
 */
export const getBillData = (billNo, billType) => {
  return getAction(PLUGIN_API + '/bill-data', { billNo, billType })
}

/**
 * 获取默认模板（按单据类型）
 * @param {string} billType - 单据类型
 */
export const getDefaultTemplateByBillType = (billType) => {
  return getAction(PLUGIN_API + '/default/' + billType)
}

/**
 * 批量获取各单据类型的默认模板
 * @param {Array} billTypes - 单据类型数组
 */
export const batchGetDefaultTemplates = (billTypes) => {
  return postAction(PLUGIN_API + '/batch-defaults', { billTypes })
}

// ========== 单据类型配置 ==========

/**
 * 获取所有支持的单据类型列表
 */
export const getBillTypeList = () => {
  return getAction(PLUGIN_API + '/bill-types')
}

/**
 * 根据单据类型获取字段配置
 * @param {string} billType - 单据类型
 */
export const getFieldsByBillType = (billType) => {
  return getAction(PLUGIN_API + '/fields/' + billType)
}

/**
 * 获取结构化字段数据（按分组：headerFields, tableColumns, companyFields）
 * @param {string} billType - 单据类型
 * @returns {Promise} - 返回 { headerFields: [], tableColumns: [], companyFields: [], allFields: [] }
 */
export const getStructuredFields = (billType) => {
  return getAction(PLUGIN_API + '/fields/structured', { billType })
}

// ========== billPrint 兼容接口（匹配参考系统）==========

/**
 * 获取票据信息 - 匹配 /api/plugin/bill-print/billPrint/info
 */
export const getBillPrintInfo = (billType) => {
  return getAction(BILLPRINT_API + '/info', { billType })
}

/**
 * 保存票据模板 - 匹配 /api/plugin/bill-print/billPrint/save
 */
export const saveBillPrintTemplate = (data) => {
  return postAction(BILLPRINT_API + '/save', data)
}

/**
 * 获取票据渲染所需的全部数据（插件聚合接口，已弃用）
 * @deprecated 请使用原始接口替代
 */
export const getAllNeedByParam = (no, billType) => {
  return getAction(BILLPRINT_API + '/getAllNeedByParam', { no, billType })
}

// ========== 原始系统接口（直接调用主系统API） ==========

/**
 * 获取单据明细列表
 * 对应原始接口：/depotItem/getDetailList
 * @param {Object} params 
 * @param {number} params.headerId - 单据ID
 * @param {string} [params.mpList] - 扩展字段列表，如 "扩展1,扩展2,扩展3"
 * @returns {Promise} - 返回 { data: { rows: [...] } }
 */
export const getDepotItemDetailList = (params) => {
  const timestamp = new Date().getTime()
  return getAction('/depotItem/getDetailList', {
    _t: timestamp,
    linkType: 'basic',
    isReadOnly: 1,
    ...params
  })
}

/**
 * 根据单据编号获取单据主体信息
 * 对应原始接口：/depotHead/getDetailByNumber
 * @param {string} number - 单据编号
 * @returns {Promise} - 返回 { data: { ...单据信息 } }
 */
export const getDepotHeadByNumber = (number) => {
  const timestamp = new Date().getTime()
  return getAction('/depotHead/getDetailByNumber', {
    _t: timestamp,
    number
  })
}

/**
 * 获取公司信息
 * 对应原始接口：/systemConfig/getCurrentInfo
 * @returns {Promise} - 返回 { data: { ...公司信息 } }
 */
export const getCurrentSystemInfo = () => {
  const timestamp = new Date().getTime()
  return getAction('/systemConfig/getCurrentInfo', { _t: timestamp })
}

// ========== 两级模板加载（新增）==========

/**
 * 按单据类型加载模板（两级回退）
 * 1. 后端：先查租户自定义，再查系统默认（tenant_id=0）
 * 2. 前端：最后兜底 assets/print_template/*.json
 * 
 * @param {string} billType 单据类型前缀（CGDD/XSDD等）
 * @returns {Promise<Object>} 模板JSON对象
 */
export const loadTemplateWithFallback = async (billType, tenantId) => {
  // 参数校验
  if (!billType) {
    console.warn('[loadTemplateWithFallback] billType为空')
    return { templateJson: { panels: [] } }
  }
  
  // 规范化 billType
  const normalizedBillType = normalizeBillType(billType)
  console.log('[loadTemplateWithFallback] normalizedBillType:', normalizedBillType)
  
  try {
    // 1. 尝试后端（两级查询已在后端实现）
    const res = await getTemplateByBillType(normalizedBillType, tenantId)
    
    if (res && res.code === 200 && res.data && res.data.templateJson) {
      console.log('[loadTemplateWithFallback] 使用后端模板, id:', res.data.id)
      
      let templateJson = res.data.templateJson
      if (typeof templateJson === 'string') {
        try {
          templateJson = JSON.parse(templateJson)
        } catch (e) {
          console.warn('[loadTemplateWithFallback] JSON解析失败:', e.message)
          templateJson = {}
        }
      }
      return {
        id: res.data.id,
        templateName: res.data.templateName,
        templateJson: templateJson,
        tenantId: res.data.tenantId,  // 返回模板的租户ID，用于判断保存时是新增还是更新
        // 返回页边距和纸张字段
        marginTop: Number(res.data.marginTop) || 0,
        marginBottom: Number(res.data.marginBottom) || 0,
        marginLeft: Number(res.data.marginLeft) || 0,
        marginRight: Number(res.data.marginRight) || 0,
        paperType: res.data.paperType,
        paperWidth: Number(res.data.paperWidth) || 0,
        paperHeight: Number(res.data.paperHeight) || 0
      }
    }
    
    console.log('[loadTemplateWithFallback] 后端无模板，回退前端')
  } catch (error) {
    console.warn('[loadTemplateWithFallback] 后端查询失败，回退前端:', error.message)
  }
  
  // 2. 回退前端 assets/print_template/*.json
  try {
    // 动态导入（避免循环依赖）
    const { getDefaultTemplateByCode } = await import('@/assets/print_template')
    const defaultTemplate = getDefaultTemplateByCode(normalizedBillType)
    
    if (defaultTemplate) {
      console.log('[loadTemplateWithFallback] 使用前端默认模板')
      return { templateJson: defaultTemplate }
    }
  } catch (error) {
    console.warn('[loadTemplateWithFallback] 前端默认模板加载失败:', error)
  }
  
  // 3. 最终兜底：返回空模板
  console.warn('[loadTemplateWithFallback] 所有回退失败，返回空模板')
  return { templateJson: { panels: [] } }
}

/**
 * 根据单据编号加载模板（自动识别单据类型）
 * 
 * @param {string} billNo 单据编号，如 "CGDD00001802221"
 * @returns {Promise<Object>} 模板JSON对象
 */
export const loadTemplateByBillNo = async (billNo) => {
  if (!billNo) {
    console.warn('[loadTemplateByBillNo] billNo为空')
    return { panels: [] }
  }
  
  // 从单据编号提取类型
  const billType = getBillTypeFromNo(billNo)
  
  if (!billType) {
    console.warn('[loadTemplateByBillNo] 无法从单据编号识别类型:', billNo)
    return { panels: [] }
  }
  
  return loadTemplateWithFallback(billType)
}

export default {
  getTemplateList,
  getList,
  getTemplateDetail,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  toggleTemplate,
  setDefaultTemplate,
  getTemplateByBillType,
  previewTemplate,
  getTemplateFields,
  getPrintUrl,
  copyTemplate,
  exportTemplates,
  importTemplatesFromFiles,
  importSingleTemplate,
  previewTemplateWithData,
  getBillData,
  getDefaultTemplateByBillType,
  batchGetDefaultTemplates,
  getBillTypeList,
  getFieldsByBillType,
  getStructuredFields,
  getBillPrintInfo,
  saveBillPrintTemplate,
  getAllNeedByParam,
  getDepotItemDetailList,
  getDepotHeadByNumber,
  getCurrentSystemInfo,
  loadTemplateWithFallback,
  loadTemplateByBillNo,
}
